import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Card,
  CardContent,
  Box,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { calcularEstadoSuscripcion, diasRestantes, colorEstado, labelEstado } from "./subscriptions/utils";

function VentaCard({ v, store, onRenovar }) {
  const subEstado = v.endDate ? calcularEstadoSuscripcion(v) : null;
  const subDias = v.endDate ? diasRestantes(v) : null;
  const showRenovar = v.endDate && ["por_vencer", "vencida"].includes(subEstado);

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <CardContent sx={{ pb: "8px !important", pt: 1.5, px: 2 }}>
        {/* Row 1: Cliente + Total */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>{v.nombreCliente}</Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(v.fecha).toLocaleDateString()}
              {v.vendedorNombre ? ` · ${v.vendedorNombre}` : ""}
            </Typography>
          </Box>
          <Typography variant="subtitle2" fontWeight={800} color="success.main" sx={{ ml: 1, flexShrink: 0 }}>
            {store.config.moneda} {v.total.toLocaleString()}
          </Typography>
        </Stack>

        {/* Row 2: Productos */}
        <Box mb={0.75}>
          {v.items?.length > 1 ? (
            v.items.map((it, idx) => (
              <Typography key={idx} variant="caption" display="block" sx={{ lineHeight: 1.4 }}>
                {it.productoNombre}{it.cantidad > 1 ? ` ×${it.cantidad}` : ""} — {store.config.moneda} {it.subtotal?.toLocaleString() || ""}
              </Typography>
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">{v.productoNombre || "—"}</Typography>
          )}
        </Box>

        {/* Row 3: Chips */}
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
          {v.esCredito ? (
            <Chip label="Crédito" size="small" color="warning" sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
          ) : (
            <>
              {v.montoEfectivo > 0 && <Chip label={`💵 ${store.config.moneda} ${v.montoEfectivo.toLocaleString()}`} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
              {v.montoTransferencia > 0 && <Chip label={`🏦 ${store.config.moneda} ${v.montoTransferencia.toLocaleString()}`} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
            </>
          )}
          {v.esCredito && v.fechaLimitePago && (
            <Chip label={`Vence: ${new Date(v.fechaLimitePago).toLocaleDateString()}`} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
          )}
          {v.leadId && <Chip label="lead" size="small" sx={{ fontSize: 10, height: 18 }} />}
          {subEstado && subEstado !== "no_aplica" && (
            <Chip
              label={`${labelEstado(subEstado)}${subDias !== null ? ` (${subDias}d)` : ""}`}
              size="small"
              sx={{ fontSize: 10, height: 18, bgcolor: colorEstado(subEstado), color: "#fff", fontWeight: 700 }}
            />
          )}
        </Stack>
      </CardContent>

      <Divider />
      <Stack direction="row" justifyContent="flex-end" px={1} py={0.25}>
        {(
          <Tooltip title={showRenovar ? "Renovar suscripción" : "Nueva venta / recompra"}>
            <IconButton size="small" color="info" onClick={() => onRenovar(v)}>
              <AutorenewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Anular venta">
          <IconButton size="small" color="error" onClick={() => store.anularVenta(v.id)}>
            <CancelIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Card>
  );
}

export default function VentasTable({ store, ventasFiltradas, onRenovar }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (isMobile) {
    return (
      <Stack spacing={1.5}>
        {ventasFiltradas.slice(0, 50).map((v) => (
          <VentaCard key={v.id} v={v} store={store} onRenovar={onRenovar} />
        ))}
        {ventasFiltradas.length === 0 && (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No hay ventas registradas
          </Typography>
        )}
      </Stack>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Fecha</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Producto</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Efectivo</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Transferencia</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Crédito</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Vence</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Vendedor</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Acc.</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ventasFiltradas.slice(0, 50).map((v) => (
            <TableRow key={v.id} hover>
              <TableCell>
                <Typography variant="caption">{new Date(v.fecha).toLocaleDateString()}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>{v.nombreCliente}</Typography>
                {v.leadId && <Chip label="lead" size="small" sx={{ fontSize: 10, height: 18 }} />}
              </TableCell>
              <TableCell>
                {v.items?.length > 1 ? (
                  <Stack spacing={0.3}>
                    {v.items.map((it, idx) => (
                      <Typography key={idx} variant="caption" sx={{ lineHeight: 1.3 }}>
                        {it.productoNombre}{it.cantidad > 1 ? ` ×${it.cantidad}` : ""}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  v.productoNombre || "—"
                )}
                {v.endDate && (() => {
                  const estado = calcularEstadoSuscripcion(v);
                  const dias = diasRestantes(v);
                  return estado !== "no_aplica" ? (
                    <Chip
                      label={`${labelEstado(estado)}${dias !== null ? ` (${dias}d)` : ""}`}
                      size="small"
                      sx={{ fontSize: 10, height: 18, ml: 0.5, bgcolor: colorEstado(estado), color: "#fff", fontWeight: 700 }}
                    />
                  ) : null;
                })()}
              </TableCell>
              <TableCell>{v.montoEfectivo > 0 ? `${store.config.moneda} ${v.montoEfectivo.toLocaleString()}` : "—"}</TableCell>
              <TableCell>{v.montoTransferencia > 0 ? `${store.config.moneda} ${v.montoTransferencia.toLocaleString()}` : "—"}</TableCell>
              <TableCell>
                <Typography fontWeight={700} color="success.main">
                  {store.config.moneda} {v.total.toLocaleString()}
                </Typography>
              </TableCell>
              <TableCell>
                {v.esCredito ? (
                  <Chip label="Crédito" size="small" color="warning" sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                ) : (
                  <Typography variant="caption" color="text.secondary">Efectivo</Typography>
                )}
              </TableCell>
              <TableCell>
                {v.esCredito && v.fechaLimitePago ? (
                  <Typography variant="caption" fontWeight={600}>
                    {new Date(v.fechaLimitePago).toLocaleDateString()}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">—</Typography>
                )}
              </TableCell>
              <TableCell>{v.vendedorNombre || "—"}</TableCell>
              <TableCell>
                {(
                  <Tooltip title="Nueva venta / recompra">
                    <IconButton size="small" color="info" onClick={() => onRenovar(v)}>
                      <AutorenewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Anular venta">
                  <IconButton size="small" color="error" onClick={() => store.anularVenta(v.id)}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {ventasFiltradas.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No hay ventas registradas</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
